import { Subject } from 'rxjs';

import { CatsRecordTypeEnum } from '@shared-enums/cats.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { limitDecimalDisplay, trimLeadingZeros } from '@shared-helpers/string-number-helpers';

import { ICatRecord } from '../interfaces/incoming';
import { ITableConfigEntity } from '../interfaces/internal-interfaces';

type ICatRecordTypeCodes = 'non' | 'emp' | 'eqp' | 'ptx' | 'pqt' | 'peq' | 'epd';

/**@description 🐈 */
export class CatsRecord {
  //this interface provides inheritable methods to each other record class handler.
  //tracked or calculated fields
  private readonly actTypeChangedSubject$ = new Subject<string>(); //this event should only occur for billable records, so that is the only subclass subscribing to this event.
  private readonly recOrderChangedSubject$ = new Subject<string>();
  __costCenter: string;
  __isModified: boolean;
  __isNew: boolean;
  __shouldDelete: boolean;
  __type: ICatRecordTypeCodes;
  __ModifierEmpno: string;
  readonly __ActtypeChanged$ = this.actTypeChangedSubject$.asObservable();
  readonly __RecOrderChanged$ = this.recOrderChangedSubject$.asObservable();
  __config?: ITableConfigEntity[]; //allows us to specify a config for the record

  //fields provided directly from cats. Many of these properties are unused for now

  /* eslint-disable @typescript-eslint/naming-convention -- These are names from SAP, not our code, so it won't comply with our naming conventions */
  Counter: string;
  Catshours: string | number;
  Quantity: string | number;
  Workdate: string;
  Employeenumber: string;
  SendCctr: string;
  Acttype: string;
  RecOrder: string;
  AbsAttType: string;
  Extdocumentno: string;
  Statkeyfig: string;
  Longtext: boolean;
  Zequipment: string;
  ZempCreatedBy: string;
  Zmatnr: string;
  ModifiedEmpno: string;
  TargetStatus: string;
  LongTextstr: string;
  Status: TimesheetStatusEnum;
  RecCctr: string;
  shouldShow: boolean;
  Zrecordtype: CatsRecordTypeEnum;
  Zperdiemamount = '';
  /* eslint-enable @typescript-eslint/naming-convention */

  constructor(type: ICatRecordTypeCodes) {
    this.__costCenter = '';
    this.__type = type;
    this.Quantity = 0;
    this.Catshours = 0;
    this.shouldShow = true;
    this.__isModified = false; //has this record been modified? if so, on sync, use the property to flag for modification
    this.__shouldDelete = false; //when sync occurs, should this record be deleted?
    this.__isNew = true;
  }

  fillStateFromSap(catRecord: ICatRecord) {
    syncObjectProperties(catRecord, this, false);
    this.__isModified = false;
    this.Catshours = Number(catRecord.Catshours);
    this.Quantity = Number(catRecord.Quantity);
    this.Zperdiemamount = limitDecimalDisplay(catRecord.Zperdiemamount);
    this.RecOrder = trimLeadingZeros(this.RecOrder);
    this.__isNew = false;
    this.shouldShow = Boolean(this.Catshours || this.Quantity); //cats will still show records with zero hours in it, and this feels weird.
    this.recOrderChangedSubject$.next(this.RecOrder);
    return this;
  }

  syncEmptyProperties(object: object) {
    syncObjectProperties(object, this, false);
    return this;
  }

  updateProperty(key: keyof this, value: any, shouldSkipUpdate = false) {
    this[key] = value;
    if (!shouldSkipUpdate) this.__isModified = true;
    if (key === 'Acttype') this.actTypeChangedSubject$.next(value);
    if (key === 'RecOrder') this.recOrderChangedSubject$.next(value);
  }

  shouldDelete(): boolean {
    if (!this.Counter) return false;
    if (this.__isNew) return false;
    return this.__shouldDelete;
  }

  shouldUpdate(): boolean {
    if (!this.Counter) return false;
    if (this.__shouldDelete) return false;
    return this.__isModified;
  }

  shouldCreate(): boolean {
    //if the record is new, and has a value, lets create. otherwise.. naw
    if (this.__shouldDelete) return false;
    if (!this.__isNew) return false;
    if (this.Counter) return false;
    let isMeaningful = Number(this.Catshours) > 0 || Number(this.Quantity) > 0;
    if (this.__type === 'epd') isMeaningful = Number(this.Zperdiemamount) > 0;
    if (this.__type === 'ptx') isMeaningful = !!this.LongTextstr; //for production text, the record is meaningful if it has a value for string.
    return isMeaningful;
  }

  getCatsFormattedRecord(): ICatRecord {
    const values: Partial<ICatRecord> = {
      Counter: this.Counter,
      Catshours: this.Catshours.toString(),
      Quantity: this.Quantity.toString(),
      Zperdiemamount: limitDecimalDisplay(this.Zperdiemamount),
      Workdate: this.Workdate,
      Employeenumber: this.Employeenumber,
      SendCctr: this.SendCctr,
      RecCctr: this.RecCctr,
      Acttype: this.Acttype,
      RecOrder: this.RecOrder,
      AbsAttType: this.AbsAttType,
      Extdocumentno: this.Extdocumentno,
      Statkeyfig: this.Statkeyfig,
      Longtext: this.Longtext,
      Zequipment: this.Zequipment,
      ZempCreatedBy: this.ZempCreatedBy,
      Zmatnr: this.Zmatnr,
      ModifiedEmpno: this.ModifiedEmpno,
      TargetStatus: this.TargetStatus,
      LongTextstr: this.LongTextstr,
      Zrecordtype: this.Zrecordtype,
    };

    if (this.Quantity) values.Quantity = this.Quantity.toString();
    if (this.Catshours) values.Catshours = this.Catshours.toString();

    //set to be undefined if not needed, and then the below reducer will remove any undefined values
    if (this.__type === 'ptx' || this.__type === 'pqt' || this.__type === 'peq' || this.__type === 'epd') {
      values.Catshours = undefined;
    } else {
      values.Quantity = undefined;
    }

    //Remove anything with undefined values
    const nonEmptyValues = Object.keys(values).reduce(
      (acc, key) => (values[key] === undefined ? { ...acc } : { ...acc, [key]: values[key] }),
      {} as ICatRecord,
    );

    return nonEmptyValues;
  }
}
